import {
    ActionType,
    currencyInCentsToString,
    genEntityId,
    stringToCurrencyInCents,
} from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClientGraphqlError } from "backend/fetcher";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Spinner } from "components/ui/spinner";
import { Formik, FormikHelpers } from "formik";
import React, { Reducer, useReducer, useState } from "react";
import { useProductName } from "store/selectors/branding";
import { useFeature, useIsAllowed } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { reportError } from "util/error-tracking";
import {
    InvoiceLineItemInput,
    InvoicePaymentScheduleInput,
    UpdateInvoiceMutationVariables,
    useDeleteInvoiceMutation,
    useUpdateInvoiceMutation,
} from "../../generated/graphql";
import { PrimaryButton } from "../ui/primary-button";
import { SecondaryButton } from "../ui/secondary-button";
import {
    apiCommissionToDraft,
    InvoiceCommissionsEditor,
    reconcileCommissionLocked,
    syncCommissionDrafts,
    validateCommissionDrafts,
} from "./invoice-commissions-editor";
import { InvoiceDetailsEditor } from "./invoice-details-editor";
import {
    ClientInvoice,
    collectSaveWarnings,
    lineItemsTotalInCents,
    validatePaymentSchedule,
} from "./invoice-helpers";
import { InvoicePaymentScheduleErrors } from "./invoice-payment-schedule-errors";
import {
    apiRuleToDraft,
    InvoiceSplitRulesEditor,
    SplitRuleDraft,
    splitRuleDraftsToApi,
} from "./invoice-split-rules-editor";
import { useDraftsSyncedWithQuery } from "./use-drafts-synced-with-query";

interface Props {
    invoice: ClientInvoice;
    close: () => void;
    refetch: () => void;
}

interface ComponentState {
    state: "closed" | "open" | "submitting";
    errorMessage: string | null;
    isWorking: boolean;
    lineItems: InvoiceLineItemInput[];
    paymentSchedule: InvoicePaymentScheduleInput | null;
}

type Action =
    | { type: "open" }
    | { type: "close" }
    | { type: "submit" }
    | { type: "submitFailure"; error: string | null }
    | { type: "submitSuccess" }
    | { type: "setLineItems"; lineItems: InvoiceLineItemInput[] }
    | { type: "setPaymentSchedule"; paymentSchedule: InvoicePaymentScheduleInput | null };

const initialState = (invoice: ClientInvoice): ComponentState => {
    return {
        state: "open",
        errorMessage: null,
        isWorking: false,
        lineItems: Array.isArray(invoice.lineItems)
            ? invoice.lineItems
            : invoice.amountCents
              ? [
                    {
                        id: genEntityId(),
                        description: "Placement",
                        quantity: 1,
                        unitPriceInCents: invoice.amountCents,
                    },
                ]
              : [],
        paymentSchedule: invoice.paymentSchedule,
    };
};
const reducer: Reducer<ComponentState, Action> = (p, a) => {
    switch (a.type) {
        case "close":
            return { ...p, state: "closed" };
        case "open":
            return { ...p, state: "open" };
        case "submit":
            return { ...p, isWorking: true };
        case "submitSuccess":
            return { ...p, state: "closed", isWorking: false, errorMessage: null };
        case "submitFailure":
            return { ...p, state: "open", isWorking: false, errorMessage: a.error };
        case "setLineItems":
            return { ...p, lineItems: a.lineItems };
        case "setPaymentSchedule":
            return { ...p, paymentSchedule: a.paymentSchedule };
    }
};

interface FormValues {
    status: string;
    paidDate: string | undefined;
    sentDate: string | undefined;
    amount: string;
    serial: string;
    notes: string | undefined;
    dueDate: string | undefined;
    externalReference: string | undefined;
}

export const InvoiceEdit: React.FC<Props> = props => {
    const { close, invoice, refetch } = props;
    const isExternallyManaged = invoice.isExternallyManaged;
    const productName = useProductName();
    const initialValues: FormValues = {
        status: invoice.status,
        paidDate: invoice.paidDate ?? undefined,
        sentDate: invoice.sentDate ?? undefined,
        amount: currencyInCentsToString(invoice.amountCents),
        serial: invoice.serial ?? undefined,
        notes: invoice.notes ?? undefined,
        dueDate: invoice.dueDate ?? undefined,
        externalReference: invoice.externalReference ?? undefined,
    };
    const [{ state, isWorking, errorMessage, lineItems, paymentSchedule }, dispatch] =
        useReducer(reducer, initialState(invoice));
    React.useEffect(() => {
        if (state === "closed") {
            close();
        }
    }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
    const updateInvoice = useUpdateInvoiceMutation();
    const deleteInvoice = useDeleteInvoiceMutation();

    const splitFeatureEnabled = useFeature("franchisePayments");
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const splitRulesListOptions = trpcClient.invoiceSplitRule.listForInvoice.queryOptions(
        { invoiceId: invoice.id },
    );
    const splitRulesQuery = useQuery({
        ...splitRulesListOptions,
        enabled: splitFeatureEnabled,
    });
    const setSplitRules = useMutation(
        trpcClient.invoiceSplitRule.setForInvoice.mutationOptions(),
    );
    const [splitDrafts, setSplitDrafts] = useState<SplitRuleDraft[]>([]);
    React.useEffect(() => {
        if (splitRulesQuery.data) {
            setSplitDrafts(
                splitRulesQuery.data.map(rule =>
                    apiRuleToDraft({ ...rule, notes: rule.notes ?? null }),
                ),
            );
        }
    }, [splitRulesQuery.data]);

    const { settings } = useTenantSettings();
    const employeeCommissionsFeatureEnabled = useFeature("employeeCommissions");
    const userCanManageEmployeeCommissions = useIsAllowed(
        ActionType.ManageEmployeeCommissions,
    );
    const employeeCommissionsSectionVisible =
        employeeCommissionsFeatureEnabled &&
        settings.employeeCommissionsEnabled &&
        userCanManageEmployeeCommissions;
    const commissionsListOptions =
        trpcClient.invoiceCommission.listForInvoice.queryOptions({
            invoiceId: invoice.id,
        });
    const commissionsQuery = useQuery({
        ...commissionsListOptions,
        enabled: employeeCommissionsSectionVisible,
    });
    const commissionTotalsOptions = trpcClient.invoiceCommission.getTotals.queryOptions({
        invoiceId: invoice.id,
    });
    const commissionTotalsQuery = useQuery({
        ...commissionTotalsOptions,
        enabled: employeeCommissionsSectionVisible,
    });
    const createCommission = useMutation(
        trpcClient.invoiceCommission.create.mutationOptions(),
    );
    const updateCommission = useMutation(
        trpcClient.invoiceCommission.update.mutationOptions(),
    );
    const removeCommission = useMutation(
        trpcClient.invoiceCommission.remove.mutationOptions(),
    );
    const { drafts: commissionDrafts, onChange: handleCommissionDraftsChange } =
        useDraftsSyncedWithQuery(
            invoice.id,
            commissionsQuery.data,
            apiCommissionToDraft,
            reconcileCommissionLocked,
        );

    const syncCommissions = React.useCallback(async () => {
        if (!employeeCommissionsSectionVisible) {
            return;
        }
        await syncCommissionDrafts(
            invoice.id,
            commissionsQuery.data ?? [],
            commissionDrafts,
            {
                create: input => createCommission.mutateAsync(input),
                update: input => updateCommission.mutateAsync(input),
                remove: input => removeCommission.mutateAsync(input),
            },
            handleCommissionDraftsChange,
        );
        queryClient.invalidateQueries({ queryKey: commissionsListOptions.queryKey });
        queryClient.invalidateQueries({ queryKey: commissionTotalsOptions.queryKey });
        queryClient.invalidateQueries({
            queryKey: trpcClient.commissionReport.listReport.queryKey(),
        });
    }, [
        employeeCommissionsSectionVisible,
        commissionsQuery.data,
        commissionDrafts,
        handleCommissionDraftsChange,
        createCommission,
        updateCommission,
        removeCommission,
        invoice.id,
        queryClient,
        commissionsListOptions.queryKey,
        commissionTotalsOptions.queryKey,
        trpcClient,
    ]);

    const submitForm = React.useCallback(
        (values: FormValues, helpers: FormikHelpers<FormValues>) => {
            const payload: UpdateInvoiceMutationVariables = {
                invoiceId: invoice.id,
                paidDate: values.paidDate === "" ? null : values.paidDate,
                sentDate: values.sentDate === "" ? null : values.sentDate,
                dueDate: values.dueDate === "" ? null : values.dueDate,
                status: values.status,
                amountCents: stringToCurrencyInCents(values.amount),
                serial: values.serial,
                notes: values.notes,
                lineItems: lineItems,
                paymentSchedule: paymentSchedule,
                externalReference: values.externalReference,
            };
            (async () => {
                const warnings = collectSaveWarnings({
                    employeeCommissionsSectionVisible,
                    invoiceTotalPaidCents: invoice.totalPaidCents,
                    originalCommissions: commissionsQuery.data ?? [],
                    commissionDrafts,
                    originalPaymentSchedule: invoice.paymentSchedule,
                    currentPaymentSchedule: paymentSchedule,
                    splitFeatureEnabled,
                    splitDrafts,
                });

                if (warnings.length === 1) {
                    const confirmed = await fancyConfirm(
                        warnings[0].title,
                        warnings[0].message,
                        "Save anyway",
                        "Go back",
                        { confirmStyle: "question" },
                    );
                    if (!confirmed) {
                        return;
                    }
                } else if (warnings.length > 1) {
                    const confirmed = await fancyConfirm(
                        "Please review before saving",
                        warnings.map(w => w.message),
                        "Save anyway",
                        "Go back",
                        { confirmStyle: "question" },
                    );
                    if (!confirmed) {
                        return;
                    }
                }

                dispatch({ type: "submit" });
                const updateErrors = isExternallyManaged
                    ? null
                    : (await updateInvoice.mutateAsync(payload)).updateInvoice.errors;
                if (updateErrors) {
                    dispatch({
                        type: "submitFailure",
                        error: updateErrors.message ?? "Sorry, there were errors.",
                    });
                    updateErrors.fields.forEach(e => {
                        helpers.setFieldError(
                            e.name === "amountCents" ? "amount" : e.name,
                            e.message,
                        );
                    });
                } else {
                    if (splitFeatureEnabled) {
                        await setSplitRules.mutateAsync({
                            invoiceId: invoice.id,
                            rules: splitRuleDraftsToApi(splitDrafts),
                        });
                        queryClient.invalidateQueries({
                            queryKey: splitRulesListOptions.queryKey,
                        });
                    }
                    try {
                        await syncCommissions();
                    } catch (error) {
                        dispatch({
                            type: "submitFailure",
                            error: "The invoice saved, but one or more commissions failed to save. Please review the Commissions section and try again.",
                        });
                        reportError(error as Error, {
                            fingerprint: "invoice-edit:sync-commissions",
                            context: { invoiceId: invoice.id },
                        });
                        return;
                    }
                    helpers.resetForm();
                    dispatch({ type: "submitSuccess" });
                    refetch();
                }
            })();
        },
        [
            dispatch,
            updateInvoice,
            isExternallyManaged,
            lineItems,
            paymentSchedule,
            invoice.id,
            invoice.totalPaidCents,
            invoice.paymentSchedule,
            refetch,
            splitFeatureEnabled,
            setSplitRules,
            splitDrafts,
            queryClient,
            splitRulesListOptions.queryKey,
            syncCommissions,
            employeeCommissionsSectionVisible,
            commissionDrafts,
            commissionsQuery.data,
        ],
    );

    const handleDelete = React.useCallback(async () => {
        try {
            await deleteInvoice.mutateAsync({ invoiceId: invoice.id });
        } catch (error) {
            const gqlError = error as ClientGraphqlError;
            if (
                typeof gqlError.hasErrorType !== "function" ||
                !gqlError.hasErrorType("NOT_FOUND")
            ) {
                throw error;
            }
        }
        dispatch({ type: "close" });
        refetch();
    }, [deleteInvoice, invoice.id, dispatch, refetch]);

    const paymentScheduleValidationErrors = validatePaymentSchedule(
        lineItems,
        paymentSchedule,
    );
    const commissionValidationErrors = validateCommissionDrafts(commissionDrafts);

    if (state === "open" || state === "submitting") {
        return (
            <div className="mb-10 space-y-4">
                <Formik onSubmit={submitForm} initialValues={initialValues}>
                    {({
                        values,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        touched,
                        setFieldValue,
                        setFieldTouched,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-6 text-base">
                                {errorMessage ? (
                                    <div className="bg-red-200 text-red-700 p-4 rounded font-bold mb-2">
                                        {errorMessage}
                                    </div>
                                ) : null}
                                <InvoiceDetailsEditor
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    values={values}
                                    touched={touched}
                                    errors={errors}
                                    lineItems={lineItems}
                                    onLineItemsChange={lineItems =>
                                        dispatch({ type: "setLineItems", lineItems })
                                    }
                                    includePaidDate={true}
                                    paymentSchedule={paymentSchedule}
                                    onPaymentScheduleChange={paymentSchedule =>
                                        dispatch({
                                            type: "setPaymentSchedule",
                                            paymentSchedule,
                                        })
                                    }
                                    onDelete={handleDelete}
                                    disabled={isExternallyManaged}
                                    deleteConfirm={
                                        isExternallyManaged
                                            ? {
                                                  title: "Delete Invoice?",
                                                  message: `This will delete the invoice from ${productName}, but will not delete the invoice from QuickBooks.\n\nAre you sure you want to continue?`,
                                                  confirmLabel: "Yes, delete",
                                                  cancelLabel: "No, cancel",
                                              }
                                            : undefined
                                    }
                                />
                                <InvoicePaymentScheduleErrors
                                    errors={paymentScheduleValidationErrors}
                                />
                                <InvoiceSplitRulesEditor
                                    rules={splitDrafts}
                                    totalCents={lineItemsTotalInCents(lineItems)}
                                    onChange={setSplitDrafts}
                                />
                                <InvoiceCommissionsEditor
                                    visible={employeeCommissionsSectionVisible}
                                    disabled={isWorking}
                                    drafts={commissionDrafts}
                                    totalCents={lineItemsTotalInCents(lineItems)}
                                    totals={commissionTotalsQuery.data}
                                    onChange={handleCommissionDraftsChange}
                                />
                                <div className="flex space-x-8 my-4 items-center">
                                    <PrimaryButton
                                        type="submit"
                                        disabled={
                                            isWorking ||
                                            paymentScheduleValidationErrors.length > 0 ||
                                            commissionValidationErrors.length > 0
                                        }>
                                        Save
                                    </PrimaryButton>
                                    <SecondaryButton
                                        onClick={e => {
                                            e.preventDefault();
                                            dispatch({ type: "close" });
                                        }}>
                                        Cancel
                                    </SecondaryButton>
                                    <div className="flex space-x-2 items-center">
                                        {isWorking ? <Spinner /> : null}
                                        {isWorking && invoice.fileId ? (
                                            <p className="text-sm italic">
                                                Re-generating PDF...
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </Formik>
            </div>
        );
    }
    return <span />;
};
